import type { ReactNode, TextareaHTMLAttributes } from 'react';
import { FormMessage } from './FormMessage';
import { useFieldA11y } from './fieldA11y';

interface TextareaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'aria-invalid'> {
  label: ReactNode;
  helperText?: string;
  error?: string;
  counterText?: ReactNode;
  containerClassName?: string;
  counterClassName?: string;
  textareaClassName?: string;
}

export const TextareaField = ({
  id,
  label,
  helperText,
  error,
  counterText,
  className,
  containerClassName,
  counterClassName,
  textareaClassName,
  required,
  ...textareaProps
}: TextareaFieldProps) => {
  const { fieldId, helperId, errorId, describedByIds } = useFieldA11y({
    id,
    describedBy: textareaProps['aria-describedby'],
    helperText,
    error,
  });

  return (
    <label
      className={[
        'form-field',
        error ? 'form-field--invalid' : '',
        containerClassName,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      htmlFor={fieldId}
    >
      <span className="form-field__label">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </span>
      <span
        className={[
          'form-field__textarea-shell',
          counterText ? 'form-field__textarea-shell--with-counter' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <textarea
          id={fieldId}
          className={['form-field__input', 'form-field__textarea', textareaClassName]
            .filter(Boolean)
            .join(' ')}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedByIds}
          {...textareaProps}
        />
        {counterText && (
          <span
            className={['form-field__counter', counterClassName]
              .filter(Boolean)
              .join(' ')}
          >
            {counterText}
          </span>
        )}
      </span>
      <FormMessage id={helperId}>{helperText}</FormMessage>
      <FormMessage id={errorId} tone="error">
        {error}
      </FormMessage>
    </label>
  );
};
